import { useEffect } from "react";

type SessionKind = "staff" | "guest";
const eventName = "nlc-session-terminated";
export const terminationKey = (kind: SessionKind) => `nlc.${kind}.signed-out`;

// Only a local sign-out marker is persisted: never a token or user detail.
// Keep it until explicit authentication succeeds, so an offline DELETE cannot
// let a still-valid HttpOnly cookie restore protected content on reload.
export function locallySignedOut(kind: SessionKind) {
  try {
    if (localStorage.getItem(terminationKey(kind)) !== null) return true;
    // Restore a cookie-backed view only when the termination marker can also
    // be persisted. Readable-but-unwritable storage (for example quota mode)
    // otherwise loses offline logout state on reload.
    const probe = `nlc.${kind}.restore-check`;
    localStorage.setItem(probe, "1"); localStorage.removeItem(probe);
    return false;
  } catch { return true; }
}
export function remoteSignOutPending(kind: SessionKind) {
  try {
    const value = localStorage.getItem(terminationKey(kind));
    const probe = `nlc.${kind}.restore-check`;
    localStorage.setItem(probe, "1"); localStorage.removeItem(probe);
    return value?.startsWith("pending:") ?? false;
  } catch { return true; }
}
export function markSignedOut(kind: SessionKind, pending = false) {
  try { localStorage.setItem(terminationKey(kind), `${pending ? "pending:" : "complete:"}${Date.now()}:${Math.random()}`); } catch { /* local clearing still works without storage */ }
  window.dispatchEvent(new CustomEvent(eventName, { detail: kind }));
}
export function allowSignedIn(kind: SessionKind) {
  try { localStorage.removeItem(terminationKey(kind)); } catch { /* storage may be disabled */ }
}
export function useSessionTermination(kind: SessionKind, expiresAt: number | undefined, terminate: () => void) {
  useEffect(() => {
    const storage = (event: StorageEvent) => {
      if (event.key === terminationKey(kind) && event.newValue !== null) terminate();
    };
    const samePage = (event: Event) => { if ((event as CustomEvent).detail === kind) terminate(); };
    window.addEventListener("storage", storage);
    window.addEventListener(eventName, samePage);
    // Recheck the absolute server expiry on resume as background-tab timers
    // may be suspended. Checking does not extend the session lifetime.
    let timer: number | undefined;
    const expire = () => {
      if (expiresAt === undefined || !Number.isFinite(expiresAt)) return;
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) { markSignedOut(kind); terminate(); }
      else {
        // Browsers may fire a timeout slightly early. Re-arm against the
        // absolute deadline instead of leaving the page authenticated forever.
        if (timer !== undefined) window.clearTimeout(timer);
        timer = window.setTimeout(expire, remaining);
      }
    };
    expire();
    window.addEventListener("focus", expire);
    document.addEventListener("visibilitychange", expire);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener("storage", storage);
      window.removeEventListener(eventName, samePage);
      window.removeEventListener("focus", expire);
      document.removeEventListener("visibilitychange", expire);
    };
  }, [kind, expiresAt, terminate]);
}
